import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { pageIndicator } from "../apollo";
import {
  ComponentTabLink,
  ITabLink,
  ITabTitle,
  PageType,
} from "../components/component-tab";
import { AsideAdminComponent } from "../layout/aside-admin";
import { Button } from "../components/button";
import { useGetNotificationSetting } from "../hooks/useGetNotificationSetting";
import { useEditNotificationSetting } from "../hooks/useEditNotificationSetting";
import {
  EditNotificationSettingInput,
  EditNotificationSettingMutation,
  GetNotificationSettingQuery,
} from "../generated/graphql";

interface INotificationSettingFormProps {
  adminNotifyPhone?: string;
  customerMessageTemplate?: string;
  orderCompleteMessageTemplate?: string;
  droneFlightMessageTemplate?: string;
}

export const AdminNotificationSetting = () => {
  const { register, handleSubmit, getValues, reset } =
    useForm<INotificationSettingFormProps>({ mode: "all" });

  const onGetCompleted = (data: GetNotificationSettingQuery) => {
    const {
      getNotificationSetting: { ok, err, setting },
    } = data;
    if (ok) {
      reset({
        adminNotifyPhone: setting?.adminNotifyPhone || "",
        customerMessageTemplate: setting?.customerMessageTemplate || "",
        orderCompleteMessageTemplate:
          setting?.orderCompleteMessageTemplate || "",
        droneFlightMessageTemplate: setting?.droneFlightMessageTemplate || "",
      });
    } else {
      alert(err);
    }
  };
  const [getNotificationSetting] =
    useGetNotificationSetting(onGetCompleted);

  useEffect(() => {
    pageIndicator("notificationSetting");
    getNotificationSetting();
  }, [getNotificationSetting]);

  const onEditCompleted = (data: EditNotificationSettingMutation) => {
    const {
      editNotificationSetting: { ok, err },
    } = data;
    if (ok) {
      alert("저장되었습니다");
    } else {
      alert(err);
    }
  };
  const [editNotificationSetting, { loading }] =
    useEditNotificationSetting(onEditCompleted);

  const onSubmit = () => {
    if (!loading) {
      const {
        adminNotifyPhone,
        customerMessageTemplate,
        orderCompleteMessageTemplate,
        droneFlightMessageTemplate,
      } = getValues();
      const input: EditNotificationSettingInput = {
        adminNotifyPhone: adminNotifyPhone || null,
        customerMessageTemplate: customerMessageTemplate || null,
        orderCompleteMessageTemplate: orderCompleteMessageTemplate || null,
        droneFlightMessageTemplate: droneFlightMessageTemplate || null,
      };
      editNotificationSetting({ variables: { input } });
    }
  };

  const tabList: ITabLink[] = [];
  const stitle: ITabTitle = { text: "알림 메시지 설정" };

  return (
    <div className="bg-main bg-opacity-50 w-full h-full min-h-screen">
      <div className="w-full mx-auto h-full min-h-screen bg-gray-100">
        <main id="main">
          <div className="grid grid-cols-[200px_1fr] max-w-full">
            <AsideAdminComponent />
            <div className="mypages bg-gray-100 w-full min-w-[1200px] overflow-hidden">
              <div className="orderWrap">
                <div className="h-fix-content relative">
                  <div className={`w-full h-full px-10 py-8`}>
                    <ComponentTabLink
                      tabs={tabList}
                      stitle={stitle}
                      type={PageType.Create}
                    />
                    <div className="shadow-lg w-full h-fix-content bg-white">
                      <form onSubmit={handleSubmit(onSubmit)} id="create">
                        <div className="pl-9 pr-11 py-8 space-y-6">
                          <div className="space-y-2">
                            <p className="font-bold text-[#4F4F4F] text-base">
                              관리자 알림 수신번호
                            </p>
                            <input
                              {...register("adminNotifyPhone", {})}
                              type="text"
                              placeholder="예) 01012345678 (비워두면 관리자 알림을 발송하지 않습니다)"
                              className="border w-full h-10 border-[#F0F0F4] text-base rounded-lg py-1 px-4"
                            />
                            <p className="text-xs text-[#8B8C8C]">
                              신규 주문이 결제 완료되면 이 번호로 알림 문자가
                              발송됩니다. 비워두면 관리자 알림은 발송되지
                              않습니다.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold text-[#4F4F4F] text-base">
                              결제완료(주문접수) 시 고객 발송 메시지
                            </p>
                            <textarea
                              {...register("customerMessageTemplate", {})}
                              rows={10}
                              placeholder={`[닐리버리 주문완료]

{name}고객님의 주문이 확인되었습니다.

■ 보내는 곳 : 닐리버리
■ 상품명 : {goodName}
■ 배송지 : {address}

닐리버리를 이용해주셔서 감사합니다!`}
                              className="border w-full border-[#F0F0F4] text-base rounded-lg py-2 px-4 leading-relaxed"
                            />
                            <p className="text-xs text-[#8B8C8C]">
                              고객이 결제를 완료해 주문이 접수되면 이 내용으로
                              문자가 발송됩니다. 아래 변수는 실제 주문
                              정보로 자동 치환됩니다: {"{name}"},{" "}
                              {"{goodName}"}, {"{receiptNum}"}, {"{address}"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold text-[#4F4F4F] text-base">
                              드론비행(배송 시작) 시 고객 발송 메시지
                            </p>
                            <textarea
                              {...register("droneFlightMessageTemplate", {})}
                              rows={10}
                              placeholder={`[닐리버리 드론배송 안내]

{name}고객님, 주문하신 상품이 드론으로 배송을 시작했습니다!

■ 상품명 : {goodName}
■ 배송지 : {address}

현재부터 약 15분 정도 소요될 예정이오니, 시간에 맞춰 픽업 장소에서 수령 부탁드립니다.
실시간 배송 현황은 닐리버리 홈페이지(https://nilivery.com/)의 마이페이지 > 주문내역조회에서 확인하실 수 있습니다.

닐리버리를 이용해주셔서 감사합니다!`}
                              className="border w-full border-[#F0F0F4] text-base rounded-lg py-2 px-4 leading-relaxed"
                            />
                            <p className="text-xs text-[#8B8C8C]">
                              주문 상태가 "드론비행"으로 바뀌면 이 내용으로
                              문자가 발송됩니다.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold text-[#4F4F4F] text-base">
                              배송완료 시 고객 발송 메시지
                            </p>
                            <textarea
                              {...register(
                                "orderCompleteMessageTemplate",
                                {}
                              )}
                              rows={10}
                              placeholder={`[닐리버리 배송완료 메시지]

{name}고객님의 상품이 배송 완료되었습니다.

■ 보내는 곳 : 닐리버리
■ 상품명 : {goodName}
■ 배송지 : {address}

닐리버리를 이용해주셔서 감사합니다!`}
                              className="border w-full border-[#F0F0F4] text-base rounded-lg py-2 px-4 leading-relaxed"
                            />
                            <p className="text-xs text-[#8B8C8C]">
                              주문 상태가 "배송완료"로 바뀌면 이 내용으로
                              문자가 발송됩니다.
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-center pt-6">
                            <Button
                              className={`w-[240px] h-[40px] bg-[#3D3E49] text-white rounded-lg`}
                              canClick={true}
                              actionText={`저장`}
                              loading={loading}
                              color={"text-white"}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
